import React from "react";
import { Grid, Typography, Box, IconButton, Container } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import CyntraLogo from "../assets/images/cyntra_logo.svg";
import fssaiimage from "../assets/images/fssai-logo-df4c.png";

import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const WebFooter = ({ storeInfo: propStoreInfo, primaryColor: propPrimaryColor }) => {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery("(min-width: 900px)");
  const reduxStoreInfo = useSelector((state) => state.sessionReducer?.store_details);
  const storeInfoConfig = useSelector((state) => state.sessionReducer?.store_config);

  const storeInfo = propStoreInfo || reduxStoreInfo;
  const storeFssai =
    storeInfo?.store_fssai ||
    storeInfo?.store?.store_fssai ||
    storeInfo?.store_fssai_lic_no ||
    storeInfo?.fssai_lic_no;
  const primaryColor =
    propPrimaryColor ||
    storeInfoConfig?.general_config?.primary_color ||
    reduxStoreInfo?.primary_color ||
    "#272727";
  return (
    <Box
      sx={{
        backgroundColor: primaryColor,
        color: "white",
        mt: 8,
        pt: 2,
        textAlign: "left",
      }}
    >
      <Container maxWidth="xl" sx={{ py: 6, px: { xs: 4, md: 6, lg: 8 } }}>
        <Grid container spacing={{ xs: 4, md: 6, lg: 8 }} justifyContent="space-between">
          {/* Left Column: Logo & Description */}
          <Grid item xs={12} md={4} lg={4}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              {storeInfo?.store_display_logo && (
                <img
                  src={storeInfo.store_display_logo}
                  alt="Store Logo"
                  style={{ maxHeight: "40px", backgroundColor: "white", borderRadius: "4px", padding: "2px" }}
                />
              )}
              <Typography variant="h6" sx={{ fontWeight: "bold", fontFamily: "Satoshi" }}>
                {storeInfo?.store_name || "The Spice Garden"}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontFamily: "Satoshi", fontSize: { xs: "14px", md: "16px" }, lineHeight: 1.6, opacity: 0.9, maxWidth: "320px" }}>
              Bringing the rich heritage of slow-cooked spices and genuine, authentic flavors directly to your home kitchen since 2015.
            </Typography>
          </Grid>

          {/* Middle Column: Quick Links */}
          <Grid item xs={12} md={3.5} lg={3} sx={{ pl: { md: 4, lg: 6 } }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", fontFamily: "Satoshi", mb: 2 }}>
              Quick Links
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {["Our Menu", "About Us", "Contact Us"].map((link) => (
                <Typography
                  key={link}
                  variant="body2"
                  onClick={() => {
                    if (isDesktop) {
                      window.scrollTo(0, 0);
                    }
                    if (link === "Our Menu") {
                      navigate("/menu");
                    } else if (link === "About Us") {
                      navigate("/about-us");
                    } else if (link === "Contact Us") {
                      navigate("/contact-us");
                    }
                  }}
                  sx={{ fontFamily: "Satoshi", fontSize: { xs: "14px", md: "16px" }, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                >
                  {link}
                </Typography>
              ))}
            </Box>
          </Grid>

          {/* Right Column: Contact & Support */}
          <Grid item xs={12} md={4.5} lg={4} sx={{ pl: { md: 4, lg: 6 } }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", fontFamily: "Satoshi", mb: 2 }}>
              Contact & Support
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PhoneIcon fontSize="small" />
                <Typography variant="body2" sx={{ fontFamily: "Satoshi", fontSize: { xs: "14px", md: "16px" } }}>
                  {storeInfo?.store_contact_no || ""}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <EmailIcon fontSize="small" />
                <Typography variant="body2" sx={{ fontFamily: "Satoshi", fontSize: { xs: "14px", md: "16px" } }}>
                  {storeInfo?.email_id || "support@example.com"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <LocationOnIcon fontSize="small" />
                <Typography variant="body2" sx={{ fontFamily: "Satoshi", fontSize: { xs: "14px", md: "16px" }, maxWidth: "280px" }}>
                  {storeInfo?.store_ad1 
                    ? `${storeInfo.store_ad1}, ${storeInfo.store_ad2 ? storeInfo.store_ad2 + ',' : ''} ${storeInfo.store_city}, ${storeInfo.store_country}, ${storeInfo.store_zip}`
                    : "2nd Floor, G&M Mall, Motijheel, Muzaffarpur City, Bihar, India, 842001"}
                </Typography>
              </Box>
            </Box>

            {/* Social Icons: YouTube, LinkedIn, Facebook, Instagram */}
            <Box sx={{ display: "flex", gap: 2, mt: 2.5 }}>
              {[
                { Icon: YouTubeIcon, name: "YouTube" },
                { Icon: LinkedInIcon, name: "LinkedIn" },
                { Icon: FacebookIcon, name: "Facebook" },
                { Icon: InstagramIcon, name: "Instagram" },
              ].map(({ Icon, name }) => (
                <IconButton
                  key={name}
                  sx={{
                    color: "white",
                    border: "1px solid white",
                    borderRadius: "50%",
                    width: "36px",
                    height: "36px",
                    "&:hover": {
                      backgroundColor: "white",
                      color: primaryColor,
                    },
                  }}
                >
                  <Icon fontSize="small" />
                </IconButton>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Copyright Bar */}
      <Box sx={{ backgroundColor: "white", color: "#333", py: 2, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 1 }}>
        {storeFssai && (
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, mb: 0.5 }}>
            <img src={fssaiimage} alt="FSSAI Logo" style={{ height: "28px" }} />
            <Typography variant="body2" sx={{ fontFamily: "Satoshi", fontSize: "0.85rem", fontWeight: 500, color: "#333" }}>
              License No. - {storeFssai}
            </Typography>
          </Box>
        )}
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
          <Typography variant="body2" sx={{ fontFamily: "Satoshi", fontSize: "0.85rem" }}>
            Powered by
          </Typography>
          <img src={CyntraLogo} alt="Cyntra" style={{ height: "20px" }} />
          <Typography variant="body2" sx={{ fontFamily: "Satoshi", fontSize: "0.85rem", ml: 1 }}>
            © Copyright All Rights Reserved
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default WebFooter;
